import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowRight, Receipt } from "@phosphor-icons/react";
import { COMPANY_BILLING_MODEL_LABEL } from "@/lib/status-configs";
import { formatLocalYmd } from "@/lib/company-expiry";
import { ExpiryNote, needsExpiryAttention } from "./ExpiryNote";
import { useSupervisorBilling } from "./use-supervisor-billing";

/** Kompaktan red na početnoj supervizora: uslovi naplate, datumi isteka i upozorenje. */
export function BillingSummaryCard() {
  const { t } = useTranslation();
  const { data } = useSupervisorBilling();
  if (!data) return null;

  const creditExpiry = data.billingModel === "credit" ? data.creditExpiresAt : null;
  const details = [
    data.cofundingPercent >= 100
      ? t("supervisor.billing.terms.cofundingFull")
      : t("supervisor.billing.terms.cofundingPartial", { percent: data.cofundingPercent }),
    data.discountPercent > 0
      ? `${t("supervisor.billing.terms.discount")} ${t("supervisor.billing.terms.percentValue", { percent: data.discountPercent })}`
      : null,
    data.contractExpiresAt ? `${t("supervisor.billing.contractShort")}: ${formatLocalYmd(data.contractExpiresAt)}` : null,
    creditExpiry ? `${t("supervisor.billing.creditShort")}: ${formatLocalYmd(creditExpiry)}` : null,
  ].filter(Boolean);

  return (
    <Link
      to="/dashboard/supervisor/billing"
      className="group flex flex-col gap-3 rounded-xl border border-border/50 bg-card px-5 py-4 shadow-sm transition-colors hover:bg-muted/20 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted/60">
          <Receipt className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{t("supervisor.billing.title")}</p>
          <p className="text-sm font-semibold text-foreground">{COMPANY_BILLING_MODEL_LABEL[data.billingModel]}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{details.join(" · ")}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {needsExpiryAttention(data.contractExpiresAt, data.today) && (
          <ExpiryNote ymd={data.contractExpiresAt!} today={data.today} prefix={t("supervisor.billing.contractShort")} />
        )}
        {needsExpiryAttention(creditExpiry, data.today) && (
          <ExpiryNote ymd={creditExpiry!} today={data.today} prefix={t("supervisor.billing.creditShort")} />
        )}
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:underline">
          {t("supervisor.billing.open")} <ArrowRight className="size-3" />
        </span>
      </div>
    </Link>
  );
}
